/**
 * Check 11 — `platform_rules`. Does this caption break the destination
 * platform's own rules, or cite a feature that no longer exists?
 *
 * THE DEFECT THIS EXISTS FOR, measured on a real artifact (2026-08-01, J6 row
 * zero, post `2c6f2e96`). One generated LinkedIn post about a Product Hunt
 * launch contained BOTH of the following, unprompted:
 *
 *   "We would love your SUPPORT for our Product Hunt launch next week!"
 *   "Check out our TEASER PAGE, hit 'NOTIFY ME', and join us on launch day."
 *
 * The first solicits votes, which Product Hunt prohibits — vote-for-vote and
 * incentivised voting can penalise the listing or the account. So a caption we
 * generated can cost the user the thing they came here to do. That is a
 * correctness failure, not a style preference.
 *
 * The second recommends Coming Soon / Ship teaser pages, which Product Hunt
 * RETIRED in August 2025. A large share of the highest-ranked launch guides
 * still describe them, so a model trained on that corpus reproduces the advice
 * confidently. To the audience most likely to notice — founders who just read
 * PH's own docs — it dates the product instantly.
 *
 * WHY THIS IS CODE AND NOT A PROMPT LINE. Both rules were written down as
 * predicted risks in `docs/planning/ICP-saas-launch-2026-08-01.md` §2 roughly
 * an hour before the walk produced both anyway. This project's own law: a rule
 * that must hold goes in code, because prose asking a model not to say
 * something is not a mechanism.
 *
 * SCOPED, ON PURPOSE. Every rule here is Product-Hunt-specific, so the check
 * only runs when the caption actually talks about a Product Hunt launch. A
 * coffee post that says "support small roasters" is not soliciting votes, and
 * flagging it would be exactly the over-firing this project has paid for
 * before. No Product Hunt mention ⇒ `skipped`, never `pass`.
 *
 * IT MUST NEVER FABRICATE A PASS. Absent caption ⇒ `skipped`. A `pass` NAMES
 * what it checked, so a human can overrule it in one glance.
 *
 * Same hard constraint as every other module here: React-free, DOM-free,
 * Node-free, `@/`-alias-free, relative imports with explicit `.ts`, so Deno can
 * reach it from the edge generator.
 */
import { CHECK_LABEL, type QualityCheck } from '../types.ts';

const ID = 'platform_rules' as const;

/** Does this caption concern a Product Hunt launch at all? */
const PRODUCT_HUNT = /\bproduct\s*hunt\b|\bproducthunt\b|#producthunt\b/i;

/**
 * Soliciting a vote. Deliberately NARROW: it targets the ask, not the noun.
 * "We launched on Product Hunt" is fine. "Upvote us" is not. "Support our
 * launch" is the polite form of the same ask and is what the live defect said,
 * so it is included — but only in the imperative/desire framing, so
 * "thanks for supporting us" in a recap does not trip it.
 */
const VOTE_SOLICITATION: readonly { pattern: RegExp; quote: string }[] = [
  // PLURALS MATTER. `\bup-?vote\b` does not match "upvotes" — the identical
  // word-boundary miss this project already recorded when `\blogo\b` failed to
  // catch "logos" in the style-reference mark filter. The live plan-layer
  // defect was literally "Drive day-of upvotes".
  { pattern: /\bup-?votes?\b/i, quote: 'upvote' },
  { pattern: /\bvotes?\s+(?:for|us|now)\b/i, quote: 'vote for us' },
  { pattern: /\bgive\s+us\s+(?:an?\s+)?(?:up-?votes?|votes?)\b/i, quote: 'give us an upvote' },
  {
    pattern: /\b(?:would\s+love|need|want|appreciate)\s+your\s+support\b/i,
    quote: 'would love your support',
  },
  { pattern: /\bsupport\s+(?:our|the|my)\s+launch\b/i, quote: 'support our launch' },
  { pattern: /\bhelp\s+us\s+(?:hit|reach|get\s+to)\s+#?\s*1\b/i, quote: 'help us hit #1' },
];

/**
 * Coming Soon / Ship teaser pages, retired 2025-08. "Notify me" is the button
 * on that dead page and is the strongest single tell.
 */
const RETIRED_FEATURE: readonly { pattern: RegExp; quote: string }[] = [
  { pattern: /\bcoming\s*soon\s*page\b/i, quote: 'coming soon page' },
  { pattern: /\bteaser\s*page\b/i, quote: 'teaser page' },
  { pattern: /\bnotify\s*me\b/i, quote: '"Notify Me"' },
  { pattern: /\bproduct\s*hunt\s*ship\b/i, quote: 'Product Hunt Ship' },
];

function firstMatch(
  caption: string,
  rules: readonly { pattern: RegExp; quote: string }[],
): string | null {
  for (const rule of rules) {
    if (rule.pattern.test(caption)) return rule.quote;
  }
  return null;
}

export function checkPlatformRules(caption: string | null | undefined): QualityCheck {
  const label = CHECK_LABEL[ID];
  const text = (caption ?? '').trim();

  if (!text) {
    return {
      id: ID,
      label,
      status: 'skipped',
      detail: 'There is no caption to check against the platform’s rules yet.',
    };
  }

  if (!PRODUCT_HUNT.test(text)) {
    return {
      id: ID,
      label,
      status: 'skipped',
      detail:
        'This caption is not about a Product Hunt launch, so the Product Hunt posting rules do not apply to it.',
    };
  }

  const solicited = firstMatch(text, VOTE_SOLICITATION);
  if (solicited) {
    return {
      id: ID,
      label,
      status: 'fail',
      detail:
        `This caption asks people to ${solicited === 'upvote' ? 'upvote' : 'back the launch'} — it says “${solicited}”. ` +
        'Product Hunt does not allow asking for votes, and a listing that does can be penalised. ' +
        'Ask people to try it and tell you what they think instead.',
    };
  }

  const retired = firstMatch(text, RETIRED_FEATURE);
  if (retired) {
    return {
      id: ID,
      label,
      status: 'fail',
      detail:
        `This caption points people at ${retired}, which Product Hunt removed in August 2025. ` +
        'Send them to your own waitlist or to the product itself.',
    };
  }

  return {
    id: ID,
    label,
    status: 'pass',
    detail:
      'This caption mentions a Product Hunt launch and does not ask for votes or point at the retired “coming soon” page.',
  };
}
